import { useMemo } from 'react';
import { useFormikContext } from 'formik';
import { CollectionOrderStates } from 'shared-pages/common/constants';
import { UserTypeValues } from 'shared-pages/shared/constants';
import { useRootStore } from 'store';

/** Default user types that can edit fields (used for all rules for now). */
const DEFAULT_USER_TYPES: UserTypeValues[] = [
  UserTypeValues.CollectionPoint,
  UserTypeValues.PlatformOwner,
];

const REQUEST_USER_TYPES: UserTypeValues[] = [UserTypeValues.CollectionPoint];

const ORGANIZE_USER_TYPES: UserTypeValues[] = [UserTypeValues.PlatformOwner];

const PACKING_USER_TYPES: UserTypeValues[] = [UserTypeValues.PlatformOwner];
const COLLECTOR_USER_TYPES: UserTypeValues[] = [UserTypeValues.Collector];

export type FieldEnabledRule = {
  /** Field is enabled only when statusId is in these states. */
  statuses: CollectionOrderStates[];
  /** Field is enabled only for these user types. Omit = all user types. */
  userTypes?: UserTypeValues[];
};

/**
 * Fields are enabled only when statusId is in the listed states AND user is one of userTypes.
 * Missing key = field is not driven by this config (treated as enabled).
 */
export const FIELDS_ENABLED_ONLY_WHEN: Record<string, FieldEnabledRule> = {
  // ---- Request tab (A1, A2, B, Γ, Δ) - editable in draft only ----
  'xrbData.msdsFileId': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.techSpecFileId': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.bmsFileId': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.manufacturer': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.partCode': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.chemistry': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.cellGeometry': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.nominalCapacity': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.nominalVoltage': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.nominalEnergyEfficiency': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.batteryWeight': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.pieceCount': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.batteryLength': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.batteryWidth': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.batteryHeight': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  // pieceDetails[*].* use same as request-tab fields (see Proxy in hook)
  'xrbData.cablesSecured': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.safetyPlugInstalled': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.certifiedPackagingIsAvailable': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.replacementPackagingIsUsed': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.certifiedPackagingCode': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.packagingCondition': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.packagingHeight': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.packagingLength': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.packagingWidth': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.packagingReturnRequired': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.bmsCharacterization': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.bmsCharacterizationOther': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.technicianAssessment': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.technicianVerifiesData': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.technicianName': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.technicianRegistrationNumber': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.packagingForCriticallyDamaged': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.carAccident': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.fire': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.naturalDisasters': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.vandalism': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.batteryLiquidCoolingSystemFailure': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.noBmsData': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.fireSmokeSignsSigns': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.oxidationSigns': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.leakageVentingSigns': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.cannotDiagnose': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.safetyDeviceTriggered': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.additionalObservations': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.easyLoadingAccess': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.specialEquipmentRequired': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.collectionPointHasEquipment': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.equipmentDescription': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.otherAccessNotes': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.batteryPhotoFileDetails': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.packagingPhotoFileDetails': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.labelsPhotoFileDetails': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.accessPhotoFileDetails': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.pieceDetails.daysOutOfOperation': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.pieceDetails.daysNotMoved': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },
  'xrbData.pieceDetails.chargeLevel': {
    statuses: [CollectionOrderStates.TemporarySaved],
    userTypes: REQUEST_USER_TYPES,
  },

  // ---- Organize tab - read-only from request vs editable in organize ----

  'xrbData.orgChemistry': {
    statuses: [CollectionOrderStates.ApplicationSubmitted],
    userTypes: ORGANIZE_USER_TYPES,
  },
  'xrbData.orgBatteryWeight': {
    statuses: [CollectionOrderStates.ApplicationSubmitted],
    userTypes: ORGANIZE_USER_TYPES,
  },
  'xrbData.orgPieceCount': {
    statuses: [CollectionOrderStates.ApplicationSubmitted],
    userTypes: ORGANIZE_USER_TYPES,
  },
  'xrbData.orgBatteryHeight': {
    statuses: [CollectionOrderStates.ApplicationSubmitted],
    userTypes: ORGANIZE_USER_TYPES,
  },
  'xrbData.orgBatteryLength': {
    statuses: [CollectionOrderStates.ApplicationSubmitted],
    userTypes: ORGANIZE_USER_TYPES,
  },
  'xrbData.orgBatteryWidth': {
    statuses: [CollectionOrderStates.ApplicationSubmitted],
    userTypes: ORGANIZE_USER_TYPES,
  },
  'xrbData.batteryCondition': {
    statuses: [CollectionOrderStates.ApplicationSubmitted],
    userTypes: ORGANIZE_USER_TYPES,
  },
  'xrbData.appropriatePackaging': {
    statuses: [CollectionOrderStates.ApplicationSubmitted],
    userTypes: ORGANIZE_USER_TYPES,
  },
  'xrbData.requiredPackaging': {
    statuses: [CollectionOrderStates.ApplicationSubmitted],
    userTypes: ORGANIZE_USER_TYPES,
  },
  'xrbData.vermiculiteUsed': {
    statuses: [CollectionOrderStates.ApplicationSubmitted],
    userTypes: ORGANIZE_USER_TYPES,
  },
  'xrbData.vermiculiteQuantity': {
    statuses: [CollectionOrderStates.ApplicationSubmitted],
    userTypes: ORGANIZE_USER_TYPES,
  },
  'xrbData.aeroplastUsed': {
    statuses: [CollectionOrderStates.ApplicationSubmitted],
    userTypes: ORGANIZE_USER_TYPES,
  },
  'xrbData.aeroplastQuantity': {
    statuses: [CollectionOrderStates.ApplicationSubmitted],
    userTypes: ORGANIZE_USER_TYPES,
  },
  'xrbData.plasticBagUsed': {
    statuses: [CollectionOrderStates.ApplicationSubmitted],
    userTypes: ORGANIZE_USER_TYPES,
  },
  'xrbData.plasticBagQuantity': {
    statuses: [CollectionOrderStates.ApplicationSubmitted],
    userTypes: ORGANIZE_USER_TYPES,
  },
  'xrbData.polyethyleneUsed': {
    statuses: [CollectionOrderStates.ApplicationSubmitted],
    userTypes: ORGANIZE_USER_TYPES,
  },
  'xrbData.polyethyleneQuantity': {
    statuses: [CollectionOrderStates.ApplicationSubmitted],
    userTypes: ORGANIZE_USER_TYPES,
  },
  'xrbData.otherMaterial': {
    statuses: [CollectionOrderStates.ApplicationSubmitted],
    userTypes: ORGANIZE_USER_TYPES,
  },

  'xrbData.totalRequestWeight': {
    statuses: [CollectionOrderStates.ApplicationSubmitted],
    userTypes: ORGANIZE_USER_TYPES,
  },

  // ---- Packing tab (future fields) ----
  // Add keys here when packing tab gets form fields.
  'xrbData.packagingInstructions': {
    statuses: [CollectionOrderStates.OrganizationCompleted],
    userTypes: PACKING_USER_TYPES,
  },
  'xrbData.packagingDispatchDate': {
    statuses: [CollectionOrderStates.OrganizationCompleted],
    userTypes: PACKING_USER_TYPES,
  },

  // ---- Collector Selection tab ----
  collectorId: {
    statuses: [
      CollectionOrderStates.OrganizationCompleted,
      CollectionOrderStates.PackageShipmentCompleted,
    ],
    userTypes: [UserTypeValues.PlatformOwner],
  },
  'xrbData.temperatureReadingRequired': {
    statuses: [
      CollectionOrderStates.OrganizationCompleted,
      CollectionOrderStates.PackageShipmentCompleted,
    ],
    userTypes: [UserTypeValues.PlatformOwner],
  },
  'xrbData.voltageReadingRequired': {
    statuses: [
      CollectionOrderStates.OrganizationCompleted,
      CollectionOrderStates.PackageShipmentCompleted,
    ],
    userTypes: [UserTypeValues.PlatformOwner],
  },
  'xrbData.packagingTypeCode': {
    statuses: [
      CollectionOrderStates.OrganizationCompleted,
      CollectionOrderStates.PackageShipmentCompleted,
    ],
    userTypes: [UserTypeValues.PlatformOwner],
  },
  'xrbData.collordPackagingReturnRequired': {
    statuses: [
      CollectionOrderStates.OrganizationCompleted,
      CollectionOrderStates.PackageShipmentCompleted,
    ],
    userTypes: [UserTypeValues.PlatformOwner],
  },
  'xrbData.wastereceptionpointId': {
    statuses: [
      CollectionOrderStates.OrganizationCompleted,
      CollectionOrderStates.PackageShipmentCompleted,
    ],
    userTypes: [UserTypeValues.PlatformOwner],
  },

  // ---- Collector Schedule or Execution tab ----
  plannedCollectionDate: {
    statuses: [CollectionOrderStates.CollectorOrder],
    userTypes: [UserTypeValues.Collector],
  },
  're-plan-CollectionDate': {
    statuses: [CollectionOrderStates.ScheduledCollectorCollection],
    userTypes: [UserTypeValues.Collector],
  },
};

/**
 * Returns a map of field name -> disabled (true/false) based on current form statusId and user type.
 * Uses Formik context for statusId and appStore for usertypeId.
 * Fields not in FIELDS_ENABLED_ONLY_WHEN are not disabled by this config.
 */
const REQUEST_TAB_ENABLED_STATUSES = [CollectionOrderStates.TemporarySaved];

function isUserAllowed(userTypes: UserTypeValues[] | undefined, usertypeId: number): boolean {
  if (!userTypes || userTypes.length === 0) return true;
  return userTypes.includes(usertypeId as UserTypeValues);
}

export function useFieldDisabledByStatus(): Record<string, boolean> {
  const { appStore } = useRootStore();
  const { values } = useFormikContext<{ statusId?: number }>();
  const statusId = values?.statusId ?? CollectionOrderStates.TemporarySaved;
  const usertypeId = appStore.usertypeId;

  return useMemo(() => {
    const out: Record<string, boolean> = {};
    for (const name of Object.keys(FIELDS_ENABLED_ONLY_WHEN)) {
      const rule = FIELDS_ENABLED_ONLY_WHEN[name];
      const statusOk = rule.statuses.includes(statusId as CollectionOrderStates);
      const userOk = isUserAllowed(rule.userTypes, usertypeId);
      out[name] = !(statusOk && userOk);
    }
    const requestTabStatusOk = REQUEST_TAB_ENABLED_STATUSES.includes(
      statusId as CollectionOrderStates,
    );
    const requestTabUserOk = isUserAllowed(DEFAULT_USER_TYPES, usertypeId);
    const requestTabDisabled = !(requestTabStatusOk && requestTabUserOk);
    return new Proxy(out, {
      get(target, prop: string) {
        if (Object.prototype.hasOwnProperty.call(target, prop)) return target[prop];
        if (
          typeof prop === 'string' &&
          /^xrbData\.pieceDetails\[\d+\]\.(daysOutOfOperation|daysNotMoved|chargeLevel|chargeLevelDate)$/.test(
            prop,
          )
        ) {
          return requestTabDisabled;
        }
        return undefined;
      },
    });
  }, [statusId, usertypeId]);
}
